export interface ReferralData {
  referral_source: string | null;
  referral_medium: string | null;
  referral_campaign: string | null;
  referral_content: string | null;
  referral_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  referrer_url: string | null;
  landing_page: string | null;
}

const STORAGE_KEY = 'vybe_referral_data';
const STORAGE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days

export const captureReferralData = (): ReferralData => {
  if (typeof window === 'undefined') {
    return createEmptyReferralData();
  }

  const urlParams = new URLSearchParams(window.location.search);

  // Extract UTM parameters
  const utm_source = urlParams.get('utm_source');
  const utm_medium = urlParams.get('utm_medium');
  const utm_campaign = urlParams.get('utm_campaign');
  const utm_content = urlParams.get('utm_content');
  const utm_term = urlParams.get('utm_term');

  // Extract click IDs
  const fbclid = urlParams.get('fbclid');
  const gclid = urlParams.get('gclid');

  // Get referrer
  const referrer = document.referrer || null;

  // Get landing page
  const landing_page = window.location.href;

  // Determine source if not explicitly set
  let source = utm_source;
  let medium = utm_medium;

  if (!source) {
    if (fbclid) {
      source = 'facebook';
      // If we have fbclid but no medium, assume it's paid
      if (!medium) {
        medium = 'cpc';
      }
    } else if (gclid) {
      source = 'google';
      if (!medium) {
        medium = 'cpc';
      }
    } else if (referrer) {
      try {
        const referrerHost = new URL(referrer).hostname;
        if (referrerHost.includes('facebook.com') || referrerHost.includes('fb.com') || referrerHost.includes('fbclid')) {
          source = 'facebook';
          // If coming from facebook.com without fbclid, might be organic social
          if (!medium) {
            medium = 'social';
          }
        } else if (referrerHost.includes('google.com')) {
          source = 'google';
          if (!medium) {
            medium = 'organic';
          }
        } else if (referrerHost.includes('instagram.com')) {
          source = 'instagram';
          if (!medium) {
            medium = 'social';
          }
        } else if (referrerHost.includes('twitter.com') || referrerHost.includes('t.co')) {
          source = 'twitter';
          if (!medium) {
            medium = 'social';
          }
        } else if (referrerHost.includes('linkedin.com')) {
          source = 'linkedin';
          if (!medium) {
            medium = 'social';
          }
        } else if (referrerHost !== window.location.hostname) {
          source = 'referral';
          if (!medium) {
            medium = 'referral';
          }
        } else {
          source = 'organic';
        }
      } catch {
        source = 'referral';
        if (!medium) {
          medium = 'referral';
        }
      }
    } else {
      source = 'direct';
      if (!medium) {
        medium = 'none';
      }
    }
  }

  const referralData: ReferralData = {
    referral_source: source,
    referral_medium: medium,
    referral_campaign: utm_campaign,
    referral_content: utm_content,
    referral_term: utm_term,
    fbclid,
    gclid,
    referrer_url: referrer,
    landing_page,
  };

  console.log('[Referral Data] Captured:', {
    source,
    medium,
    campaign: utm_campaign,
    fbclid: fbclid ? 'present' : 'none',
    gclid: gclid ? 'present' : 'none',
    referrer,
    url: landing_page
  });

  return referralData;
};

export const saveReferralData = (data: ReferralData): void => {
  if (typeof window === 'undefined') return;

  try {
    const storageData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
  } catch (error) {
    console.error('Failed to save referral data:', error);
  }
};

export const getReferralData = (): ReferralData | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const { data, timestamp } = JSON.parse(stored);

    // Check if data is still valid (within 30 days)
    if (Date.now() - timestamp > STORAGE_DURATION) {
      clearReferralData();
      return null;
    }

    return data;
  } catch (error) {
    console.error('Failed to retrieve referral data:', error);
    return null;
  }
};

export const clearReferralData = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear referral data:', error);
  }
};

export const initializeReferralTracking = (): void => {
  if (typeof window === 'undefined') return;

  // Check for marketing parameters
  const hasUtmParams = window.location.search.includes('utm_') ||
                       window.location.search.includes('fbclid') ||
                       window.location.search.includes('gclid');

  const existingData = getReferralData();

  // Capture new data if:
  // 1. No existing data, OR
  // 2. There are UTM/click params (which take precedence)
  if (!existingData || hasUtmParams) {
    const newData = captureReferralData();
    saveReferralData(newData);
    console.log('[Referral Tracking] Captured and saved:', newData);
    console.log('[Referral Tracking] URL:', window.location.href);
    console.log('[Referral Tracking] Referrer:', document.referrer);
  } else {
    console.log('[Referral Tracking] Using existing data:', existingData);
  }
};

const createEmptyReferralData = (): ReferralData => ({
  referral_source: null,
  referral_medium: null,
  referral_campaign: null,
  referral_content: null,
  referral_term: null,
  fbclid: null,
  gclid: null,
  referrer_url: null,
  landing_page: null,
});
